import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  getArticoli, getFornitori, getCategorieArticoli,
  updateGiacenza, updateSoglia, getMovimentiInventario, aggiungiSottoSogliaALista,
  getPrevisioniConsumi
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Boxes, AlertTriangle, Minus, Plus, History, ShoppingBasket,
  Search, Filter, Save, Clock, User, TrendingDown, Calendar
} from 'lucide-react';

const SENZA_CAT = '__senza__';
const TUTTE = 'all';

// ============== ROW INVENTARIO ==============
const InventarioRow = ({ articolo, isAdmin, onUpdateGiacenza, onEditSoglia, onShowStorico }) => {
  const [val, setVal] = useState(String(articolo.giacenza ?? 0));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Sync se cambia l'articolo dall'esterno
  useEffect(() => {
    setVal(String(articolo.giacenza ?? 0));
    setDirty(false);
  }, [articolo.giacenza]);

  const giac = parseFloat(val) || 0;
  const soglia = parseFloat(articolo.soglia_minima) || 0;
  const sottoSoglia = soglia > 0 && giac < soglia;

  const stepBy = async (delta) => {
    const nuovo = Math.max(0, giac + delta);
    setVal(String(nuovo));
    setSaving(true);
    try {
      await onUpdateGiacenza(articolo.id, { giacenza: nuovo, tipo: delta > 0 ? 'carico' : 'scarico' });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const saveDirect = async () => {
    if (parseFloat(val) < 0) {
      toast.error('Giacenza non può essere negativa');
      return;
    }
    setSaving(true);
    try {
      await onUpdateGiacenza(articolo.id, { giacenza: parseFloat(val) || 0, tipo: 'conteggio' });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableRow data-testid={`inv-row-${articolo.id}`} className={sottoSoglia ? 'bg-red-500/5' : ''}>
      <TableCell>
        <div className="flex items-center gap-2">
          {sottoSoglia && <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />}
          <div className="min-w-0">
            <p className="font-medium truncate">{articolo.nome}</p>
            {articolo.fornitore_nome && (
              <p className="text-xs text-muted-foreground truncate">{articolo.fornitore_nome}</p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        {articolo.categoria ? (
          <Badge variant="secondary" className="font-normal">{articolo.categoria}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground italic">—</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={saving || giac <= 0}
            onClick={() => stepBy(-1)}
            data-testid={`dec-${articolo.id}`}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <Input
            type="number"
            min="0"
            step="any"
            value={val}
            onChange={(e) => { setVal(e.target.value); setDirty(true); }}
            onBlur={() => { if (dirty) saveDirect(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
            className="w-20 h-8 text-center font-mono"
            data-testid={`giac-input-${articolo.id}`}
          />
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={saving}
            onClick={() => stepBy(1)}
            data-testid={`inc-${articolo.id}`}
          >
            <Plus className="h-3 w-3" />
          </Button>
          <span className="text-xs text-muted-foreground ml-1 w-8">{articolo.unita_misura || 'pz'}</span>
        </div>
      </TableCell>
      <TableCell>
        {soglia > 0 ? (
          <Badge variant={sottoSoglia ? 'destructive' : 'outline'} className="font-mono">
            {soglia} {articolo.unita_misura || ''}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground italic">—</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onShowStorico(articolo)}
            title="Storico"
            data-testid={`storico-${articolo.id}`}
          >
            <History className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditSoglia(articolo)}
              title="Imposta soglia"
              data-testid={`edit-soglia-${articolo.id}`}
            >
              <Filter className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

// ============== DIALOG SOGLIA ==============
const SogliaDialog = ({ articolo, open, onClose, onSaved }) => {
  const [soglia, setSoglia] = useState('');
  const [quantitaRiordino, setQuantitaRiordino] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (articolo) {
      setSoglia(String(articolo.soglia_minima ?? 0));
      setQuantitaRiordino(String(articolo.quantita_riordino ?? 0));
    }
  }, [articolo]);

  if (!articolo) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSoglia(articolo.id, {
        soglia_minima: parseFloat(soglia) || 0,
        quantita_riordino: parseFloat(quantitaRiordino) || 0,
      });
      toast.success('Soglia aggiornata');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]" data-testid="soglia-dialog">
        <DialogHeader>
          <DialogTitle>Soglia di riordino</DialogTitle>
          <DialogDescription>{articolo.nome}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="soglia">Soglia minima</Label>
            <Input
              id="soglia"
              type="number"
              min="0"
              step="any"
              value={soglia}
              onChange={(e) => setSoglia(e.target.value)}
              placeholder="Es. 5"
              data-testid="soglia-input"
            />
            <p className="text-xs text-muted-foreground">
              Quando la giacenza scende sotto questo valore, scatta l'avviso. Imposta a 0 per disabilitare.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="riordino">Quantità di riordino</Label>
            <Input
              id="riordino"
              type="number"
              min="0"
              step="any"
              value={quantitaRiordino}
              onChange={(e) => setQuantitaRiordino(e.target.value)}
              placeholder="Es. 12"
              data-testid="riordino-input"
            />
            <p className="text-xs text-muted-foreground">
              Quanti pezzi suggerire di ricomprare quando aggiungi alla Lista Spesa. Lascia 0 per usare soglia × 2.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-soglia-btn">
            <Save className="h-4 w-4 mr-2" />
            Salva
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============== DIALOG STORICO ==============
const StoricoDialog = ({ articolo, open, onClose }) => {
  const [movimenti, setMovimenti] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && articolo) {
      setLoading(true);
      getMovimentiInventario(articolo.id, 50)
        .then(setMovimenti)
        .catch(() => toast.error('Errore caricamento storico'))
        .finally(() => setLoading(false));
    }
  }, [open, articolo]);

  if (!articolo) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" data-testid="storico-dialog">
        <DialogHeader>
          <DialogTitle>Storico inventario</DialogTitle>
          <DialogDescription>{articolo.nome}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Caricamento...</div>
        ) : movimenti.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Nessun movimento registrato per questo articolo.
          </div>
        ) : (
          <div className="space-y-2">
            {movimenti.map((m) => {
              const deltaPositive = m.delta > 0;
              return (
                <div key={m.id} className="flex items-start gap-3 p-3 border rounded-lg" data-testid={`mov-${m.id}`}>
                  <div className={`flex-shrink-0 w-1 self-stretch rounded ${
                    m.tipo === 'carico' ? 'bg-emerald-500' :
                    m.tipo === 'scarico' ? 'bg-amber-500' :
                    m.tipo === 'rettifica' ? 'bg-blue-500' : 'bg-muted-foreground'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs capitalize">{m.tipo}</Badge>
                      <span className="font-mono text-sm">
                        {m.giacenza_prima} → <strong>{m.giacenza_dopo}</strong>
                      </span>
                      <Badge variant={deltaPositive ? 'default' : 'secondary'} className="font-mono">
                        {deltaPositive ? '+' : ''}{m.delta}
                      </Badge>
                    </div>
                    {m.note && (
                      <p className="text-xs text-muted-foreground italic mt-1">"{m.note}"</p>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {m.fatto_da_nome}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(m.fatto_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============== TAB PREVISIONI CONSUMI ==============
const PrevisioniTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('30');

  const load = async (g) => {
    setLoading(true);
    try {
      const res = await getPrevisioniConsumi(parseInt(g, 10));
      setData(res);
    } catch (error) {
      toast.error('Errore caricamento previsioni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(periodo);
  }, [periodo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getAutonomiaColor = (giorni) => {
    if (giorni === null) return 'text-muted-foreground';
    if (giorni < 3) return 'text-red-500';
    if (giorni < 7) return 'text-amber-500';
    if (giorni < 14) return 'text-yellow-600';
    return 'text-emerald-600';
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <TrendingDown className="h-5 w-5" />
              Previsione consumi
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Consumo medio giornaliero e giorni di autonomia residua basati sui movimenti registrati.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Periodo:</Label>
            <Select value={periodo} onValueChange={setPeriodo}>
              <SelectTrigger className="w-[140px]" data-testid="periodo-previsioni">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                <SelectItem value="14">Ultimi 14 giorni</SelectItem>
                <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                <SelectItem value="60">Ultimi 60 giorni</SelectItem>
                <SelectItem value="90">Ultimi 90 giorni</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {data.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              Nessun consumo registrato nel periodo selezionato.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Servono movimenti di scarico o decremento giacenza per generare previsioni.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Articolo</TableHead>
                    <TableHead>Giacenza</TableHead>
                    <TableHead>Consumo/giorno</TableHead>
                    <TableHead>Autonomia</TableHead>
                    <TableHead>Giorni a soglia</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Movimenti</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((p) => (
                    <TableRow key={p.articolo_id} data-testid={`prev-row-${p.articolo_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{p.articolo_nome}</p>
                          {p.fornitore_nome && (
                            <p className="text-xs text-muted-foreground">{p.fornitore_nome}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">
                        {p.giacenza} {p.unita_misura}
                      </TableCell>
                      <TableCell className="font-mono">
                        {p.consumo_giornaliero} {p.unita_misura}/g
                      </TableCell>
                      <TableCell>
                        {p.giorni_autonomia !== null ? (
                          <span className={`font-mono font-semibold ${getAutonomiaColor(p.giorni_autonomia)}`}>
                            {p.giorni_autonomia} g
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.giorni_a_soglia !== null ? (
                          <Badge variant={p.giorni_a_soglia < 3 ? 'destructive' : 'outline'} className="font-mono">
                            {p.giorni_a_soglia} g
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">—</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-right text-xs text-muted-foreground">
                        {p.n_movimenti} mov. / {p.span_giorni}g
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== PAGINA ==============
const Inventario = () => {
  const [articoli, setArticoli] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState(TUTTE);
  const [filterFornitore, setFilterFornitore] = useState(TUTTE);
  const [onlySottoSoglia, setOnlySottoSoglia] = useState(false);
  const [editingSoglia, setEditingSoglia] = useState(null);
  const [showingStorico, setShowingStorico] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [art, forn, cat] = await Promise.all([
        getArticoli(),
        getFornitori(),
        getCategorieArticoli(),
      ]);
      setArticoli(art);
      setFornitori(forn);
      setCategorie(cat);
    } catch (error) {
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGiacenza = async (id, payload) => {
    try {
      const updated = await updateGiacenza(id, payload);
      setArticoli((prev) => prev.map((a) => a.id === id ? { ...a, ...updated } : a));
      toast.success(`Giacenza aggiornata: ${updated.giacenza}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento');
      // ricarica per riallineare
      loadAll();
    }
  };

  const handleSogliaSaved = () => {
    loadAll();
  };

  const handleAggiungiTuttiSottoSoglia = async () => {
    if (!window.confirm('Aggiungere tutti gli articoli sotto soglia alla Lista Spesa?')) return;
    setBulkLoading(true);
    try {
      const result = await aggiungiSottoSogliaALista(null);
      toast.success(result.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setBulkLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = articoli;
    if (filterCat !== TUTTE) {
      list = list.filter((a) => filterCat === SENZA_CAT ? !a.categoria : a.categoria === filterCat);
    }
    if (filterFornitore !== TUTTE) {
      list = list.filter((a) => a.fornitore_id === filterFornitore);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((a) => a.nome.toLowerCase().includes(q));
    }
    if (onlySottoSoglia) {
      list = list.filter((a) => {
        const s = parseFloat(a.soglia_minima) || 0;
        const g = parseFloat(a.giacenza) || 0;
        return s > 0 && g < s;
      });
    }
    return list.slice().sort((a, b) => a.nome.localeCompare(b.nome));
  }, [articoli, filterCat, filterFornitore, search, onlySottoSoglia]);

  const sottoSogliaCount = useMemo(() =>
    articoli.filter((a) => {
      const s = parseFloat(a.soglia_minima) || 0;
      const g = parseFloat(a.giacenza) || 0;
      return s > 0 && g < s;
    }).length
  , [articoli]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="inventario-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Boxes className="h-7 w-7" />
            Inventario
          </h1>
          <p className="text-muted-foreground mt-1">
            Conta le giacenze e gestisci le soglie minime di riordino.
          </p>
        </div>
        {sottoSogliaCount > 0 && (
          <Button
            variant="default"
            onClick={handleAggiungiTuttiSottoSoglia}
            disabled={bulkLoading}
            data-testid="add-all-sotto-soglia-btn"
          >
            <ShoppingBasket className="h-4 w-4 mr-2" />
            {bulkLoading ? 'Aggiunta...' : `Aggiungi ${sottoSogliaCount} sotto soglia alla Lista`}
          </Button>
        )}
      </div>

      <Tabs defaultValue="conteggio" className="space-y-4">
        <TabsList>
          <TabsTrigger value="conteggio" data-testid="tab-conteggio">
            <Boxes className="h-4 w-4 mr-2" />
            Conteggio giacenze
          </TabsTrigger>
          <TabsTrigger value="previsioni" data-testid="tab-previsioni">
            <TrendingDown className="h-4 w-4 mr-2" />
            Previsioni consumi
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conteggio" className="space-y-4">
          <Card>
            <CardContent className="p-4 flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca articolo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                  data-testid="search-inv-input"
                />
              </div>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-[180px]" data-testid="filter-cat-inv">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TUTTE}>Tutte le categorie</SelectItem>
                  <SelectItem value={SENZA_CAT}>Senza categoria</SelectItem>
                  {categorie.map((c) => (
                    <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterFornitore} onValueChange={setFilterFornitore}>
                <SelectTrigger className="w-[180px]" data-testid="filter-forn-inv">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TUTTE}>Tutti i fornitori</SelectItem>
                  {fornitori.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant={onlySottoSoglia ? 'destructive' : 'outline'}
                onClick={() => setOnlySottoSoglia((v) => !v)}
                data-testid="toggle-sotto-soglia"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Sotto soglia {sottoSogliaCount > 0 && `(${sottoSogliaCount})`}
              </Button>
            </CardContent>
          </Card>

          {filtered.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Boxes className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  {onlySottoSoglia ? 'Nessun articolo sotto soglia. Ottimo lavoro!' : 'Nessun articolo trovato.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">Articolo</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead className="min-w-[200px]">Giacenza</TableHead>
                        <TableHead>Soglia</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((a) => (
                        <InventarioRow
                          key={a.id}
                          articolo={a}
                          isAdmin={isAdmin}
                          onUpdateGiacenza={handleUpdateGiacenza}
                          onEditSoglia={setEditingSoglia}
                          onShowStorico={setShowingStorico}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="previsioni" className="space-y-4">
          <PrevisioniTab />
        </TabsContent>
      </Tabs>

      <SogliaDialog
        articolo={editingSoglia}
        open={!!editingSoglia}
        onClose={() => setEditingSoglia(null)}
        onSaved={handleSogliaSaved}
      />
      <StoricoDialog
        articolo={showingStorico}
        open={!!showingStorico}
        onClose={() => setShowingStorico(null)}
      />
    </div>
  );
};

export default Inventario;
